import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Eye, EyeOff } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import RSVPFieldEditor from './RSVPFieldEditor';
import RSVPFieldList from './RSVPFieldList';
import RSVPFieldPreview from './RSVPFieldPreview';
import './RSVPFieldManager.css';

const RSVPFieldManager = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.ADMIN_RSVP_FIELDS);
      if (response.data.success) {
        setFields(response.data.fields || []);
      } else {
        setError('Failed to load fields');
      }
    } catch (err) {
      console.error('Error fetching fields:', err);
      setError('Error loading fields');
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = () => {
    setEditingField(null);
    setShowEditor(true);
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setShowEditor(true);
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Are you sure you want to delete this field? Existing responses will be preserved.')) {
      return;
    }

    try {
      const response = await axios.delete(API_ENDPOINTS.ADMIN_RSVP_FIELDS, {
        data: { field_id: fieldId }
      });

      if (response.data.success) {
        alert('Field deleted successfully!');
        fetchFields();
      } else {
        alert(response.data.message || 'Error deleting field');
      }
    } catch (err) {
      console.error('Error deleting field:', err);
      alert('Error deleting field');
    }
  };

  const handleReorderFields = async (reorderedFields) => {
    try {
      const fieldOrders = reorderedFields.map((field, index) => ({
        id: field.id,
        sort_order: index
      }));

      const response = await axios.put(API_ENDPOINTS.ADMIN_RSVP_FIELDS, {
        action: 'reorder',
        field_orders: fieldOrders
      });

      if (response.data.success) {
        setFields(reorderedFields);
      } else {
        alert('Error reordering fields');
      }
    } catch (err) {
      console.error('Error reordering fields:', err);
      alert('Error reordering fields');
    }
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingField(null);
  };

  const handleEditorSave = () => {
    fetchFields();
    setShowEditor(false);
    setEditingField(null);
  };

  if (loading) {
    return <div className="loading">Loading RSVP fields...</div>;
  }

  if (error) {
    return <div className="error-state">{error}</div>;
  }

  return (
    <div className="rsvp-field-manager">
      <div className="section-header">
        <h2>Customize RSVP Form Fields</h2>
        <div className="header-actions">
          <button 
            className={`btn-secondary ${previewMode ? 'active' : ''}`}
            onClick={() => setPreviewMode(!previewMode)}
          >
            {previewMode ? <EyeOff size={18} /> : <Eye size={18} />}
            {previewMode ? 'Hide Preview' : 'Preview Form'}
          </button>
          <button className="btn-primary" onClick={handleAddField}>
            <Plus size={18} /> Add Field
          </button>
        </div>
      </div>

      {previewMode ? (
        <RSVPFieldPreview fields={fields} />
      ) : (
        <RSVPFieldList
          fields={fields}
          onEdit={handleEditField}
          onDelete={handleDeleteField}
          onReorder={handleReorderFields}
        />
      )}

      {showEditor && (
        <RSVPFieldEditor
          field={editingField}
          onClose={handleEditorClose}
          onSave={handleEditorSave}
        />
      )}
    </div>
  );
};

export default RSVPFieldManager;
